import { DestroyRef, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { exhaustMap, finalize, interval, Observable, Subject, Subscription, timer } from 'rxjs';
import { GuidedSetupStore, VerificationResult } from './guided-setup.store';
import { CliVerificationService } from '../shared/services/cli-verification.service';
import { LoggingService } from '../shared/services/logging.service';
import { LOG_EVENTS } from '../shared/services/log-events';
import {
	CliVerificationAttemptResponse,
	CliVerificationAttemptStatus,
	CliVerificationTriggerResponse,
	parseBerlinLocalDateTime,
} from '../shared/models/cli-verification.model';
import { familyOfStatus, familyOfTrigger } from '../shared/helpers/cli-verification-outcome.helper';

export const CLI_POLL_INTERVAL_MS = 2_000;
/**
 * How long past the server's own `deadline` the page keeps polling before it gives up on its own.
 * The server's reaper closes the attempt as TIMED_OUT at the deadline; the grace only covers the
 * reaper's batch interval, so the page does not wait forever for an answer that is never coming.
 */
export const CLI_DEADLINE_GRACE_MS = 30_000;
/**
 * Upper bound for a call whose answer carried neither `deadline` nor `expiresAt` (or neither could
 * be read): the page must still give up on its own instead of polling forever.
 */
export const CLI_FALLBACK_MAX_MS = 5 * 60_000;
/** Consecutive failed polls after which the page stops and shows "our side". */
export const CLI_MAX_POLL_ERRORS = 5;
/**
 * The page never gives up sooner than this after the trigger answered, whatever the server's
 * deadline says. The deadline is Berlin-local server time compared with the client's clock; a
 * client running fast would otherwise give up on the first tick while the phone still rings.
 */
export const CLI_MIN_CALL_WINDOW_MS = 120_000;
/** Trigger refusals about the account, not the number: entering another number would not help. */
const ACCOUNT_REFUSALS: readonly string[] = ['NOT_ELIGIBLE_NO_SIP_TRUNK', 'IMPERSONATED_SESSION'];
/** One clock tick for the countdowns. */
const TICK_MS = 1_000;
/** How long the "verified" confirmation stays before the overview follows by itself (EVA wizard pattern). */
export const VERIFIED_AUTO_CONTINUE_SECONDS = 5;

/**
 * The running part of the own-number verification: the trigger, the code, the polling, cancel.
 *
 * Provided on the `GuidedSetup` component, NOT in root, and that is the security property: the code
 * is a live credential and must not outlive the dialog. Closing the dialog destroys this instance
 * and with it the code, the polling and the countdown. A resumed run goes back to the verify
 * screen; re-triggering while the call is still running returns the same code (server-side
 * idempotency), so nothing is lost by not keeping it.
 *
 * Everything the result screens need goes to the root store, which never sees the code.
 */
@Injectable()
export class GuidedSetupVerification {
	private readonly store = inject(GuidedSetupStore);
	private readonly service = inject(CliVerificationService);
	private readonly logger = inject(LoggingService);

	/** The four digits to say on the phone. Only ever set from a TRIGGERED answer. */
	readonly code = signal<string | null>(null);
	readonly status = signal<CliVerificationAttemptStatus | null>(null);
	readonly expiresAt = signal<Date | null>(null);
	/** Drives the countdown; ticks once a second while a call runs. */
	readonly now = signal(Date.now());
	/** Seconds until the confirmation continues to the overview, or null when not counting. */
	readonly autoContinueIn = signal<number | null>(null);

	/** Emits when a request answered 401: the component sends the customer to sign in again. */
	readonly unauthorized$ = new Subject<void>();

	private readonly destroyRef = inject(DestroyRef);
	private attemptId: number | null = null;
	/** When the page gives up on its own: the server's deadline (or expiry, or a fallback) plus the grace. */
	private giveUpAt: number | null = null;
	private pollErrors = 0;
	private polling: Subscription | null = null;
	/** The one question asked at the deadline; cancelled with the polling so it cannot override a click. */
	private finalCheck: Subscription | null = null;
	private ticking: Subscription | null = null;
	private autoContinue: Subscription | null = null;

	constructor() {
		/*
		 * A phone freezes the page while the call app is in front, and hidden tabs throttle timers.
		 * Back in view, the attempt is asked at once instead of waiting for a tick that may already be
		 * past the deadline.
		 */
		const document = inject(DOCUMENT);
		const onVisible = (): void => {
			if (document.visibilityState === 'visible') {
				this.pollNow();
			}
		};
		document.addEventListener('visibilitychange', onVisible);
		this.destroyRef.onDestroy(() => {
			document.removeEventListener('visibilitychange', onVisible);
			this.stop();
			this.stopAutoContinue();
		});
	}

	/**
	 * Every one-shot request is bound to the dialog's life. The header close stays
	 * usable while a trigger is out (it can take 20 s), and a late answer must not reach a destroyed
	 * instance: it would store the code again, write the root store's phase under a reopened dialog
	 * and start polling that nothing ever stops. Unsubscribing cancels the request; `finalize` frees
	 * the shared busy flag, which the next run would otherwise inherit.
	 */
	private bound<T>(request$: Observable<T>): Observable<T> {
		return request$.pipe(
			takeUntilDestroyed(this.destroyRef),
			finalize(() => this.store.busy.set(false)),
		);
	}

	/** Leaves the confirmation for the overview - by the button, or when the countdown runs out. */
	continueToOverview(): void {
		this.stopAutoContinue();
		this.store.phase.set('booked');
	}

	/**
	 * Asks for the call. The trigger button is gone while this runs (the screen shows the busy
	 * label), and the server is idempotent per running call, so a double press cannot ring twice.
	 */
	trigger(phoneServiceId: number, phoneNumber: string, declarationText: string): void {
		if (this.store.busy()) {
			return;
		}
		this.store.busy.set(true);
		this.store.verificationResult.set(null);
		this.store.manualRequestSent.set(false);
		this.store.manualRequestFailed.set(false);
		this.bound(this.service.trigger$(phoneServiceId, phoneNumber, declarationText)).subscribe({
			next: (response) => {
				this.store.busy.set(false);
				this.onTrigger(phoneServiceId, response);
			},
			error: (error: unknown) => {
				this.store.busy.set(false);
				if (isUnauthorized(error)) {
					this.unauthorized$.next();
					return;
				}
				this.logger.error(LOG_EVENTS.failed, {
					stage: 'verification',
					reason: 'trigger failed: ' + describe(error),
					phoneServiceId,
				});
				this.showResult({ family: 'ourSide', retryAt: null, remainingTotal: null });
			},
		});
	}

	/**
	 * Ends the running call. The server may have finished it a moment earlier - then it answers with
	 * the state it ended in, which is shown like any poll result. Only CANCELLED goes back to the
	 * verify screen. `then` runs after a real cancel, e.g. to move on to the number step.
	 */
	cancel(phoneServiceId: number, then?: () => void): void {
		/* The buttons are disabled while busy; this keeps a second cancel from unlocking a re-trigger that is still out. */
		if (this.store.busy()) {
			return;
		}
		const attemptId = this.attemptId;
		this.stopPolling();
		if (attemptId === null) {
			this.backToVerify(then);
			return;
		}
		this.store.busy.set(true);
		this.bound(this.service.cancel$(phoneServiceId, attemptId)).subscribe({
			next: (response) => {
				this.store.busy.set(false);
				const family = familyOfStatus(response.status);
				if (family === 'cancelled' || family === 'running') {
					this.backToVerify(then);
					return;
				}
				this.finish(response);
			},
			error: (error: unknown) => {
				this.store.busy.set(false);
				if (isUnauthorized(error)) {
					this.unauthorized$.next();
					return;
				}
				// The code may still be live on the server; it expires on its own. Going back is safe:
				// a new trigger for the same number returns the running attempt.
				this.backToVerify(then);
			},
		});
	}

	requestManual(phoneServiceId: number, phoneNumber: string, note: string): void {
		if (this.store.busy()) {
			return;
		}
		this.store.busy.set(true);
		this.store.manualRequestFailed.set(false);
		this.bound(this.service.requestManual$(phoneServiceId, phoneNumber, note.trim())).subscribe({
			next: () => {
				this.store.busy.set(false);
				this.store.manualRequestSent.set(true);
			},
			error: (error: unknown) => {
				this.store.busy.set(false);
				if (isUnauthorized(error)) {
					this.unauthorized$.next();
					return;
				}
				this.store.manualRequestFailed.set(true);
				this.logger.error(LOG_EVENTS.failed, {
					stage: 'verification',
					reason: 'manual request failed: ' + describe(error),
					phoneServiceId,
				});
			},
		});
	}

	/** Seconds left on the code, or null when there is nothing to count down. */
	get secondsLeft(): number | null {
		const expiresAt = this.expiresAt();
		if (!expiresAt) {
			return null;
		}
		return Math.max(0, Math.floor((expiresAt.getTime() - this.now()) / 1000));
	}

	/** Forgets the code and everything about the attempt. */
	stop(): void {
		this.stopPolling();
		this.code.set(null);
		this.status.set(null);
		this.expiresAt.set(null);
		this.attemptId = null;
		this.giveUpAt = null;
		this.pollErrors = 0;
	}

	/**
	 * The customer did something on the confirmation screen: the countdown stops and the overview
	 * waits for the continue button (`SIP_BTN_CONTINUE`; WCAG 2.2.1 - nobody is moved on
	 * mid-sentence).
	 */
	holdAutoContinue(): void {
		if (this.autoContinue) {
			this.stopAutoContinue();
		}
	}

	private onTrigger(phoneServiceId: number, response: CliVerificationTriggerResponse): void {
		const family = familyOfTrigger(response.outcome);
		this.logger.usage(LOG_EVENTS.verificationTriggered, { outcome: response.outcome, phoneServiceId });

		if (family !== 'running') {
			this.showResult({
				family,
				retryAt: parseBerlinLocalDateTime(response.retryAfter)?.toISOString() ?? null,
				remainingTotal: null,
				numberChangeable: !ACCOUNT_REFUSALS.includes(response.outcome),
			});
			return;
		}
		if (response.attemptId == null || !response.code) {
			this.logger.error(LOG_EVENTS.failed, {
				stage: 'verification',
				reason: 'TRIGGERED without attempt or code',
				phoneServiceId,
			});
			this.showResult({ family: 'ourSide', retryAt: null, remainingTotal: null });
			return;
		}

		this.attemptId = response.attemptId;
		this.code.set(response.code);
		this.status.set(response.status ?? 'IN_FLIGHT');
		const expiresAt = parseBerlinLocalDateTime(response.expiresAt);
		this.expiresAt.set(expiresAt);
		const end = parseBerlinLocalDateTime(response.deadline) ?? expiresAt;
		const serverGiveUp = (end ? end.getTime() : Date.now() + CLI_FALLBACK_MAX_MS) + CLI_DEADLINE_GRACE_MS;
		this.giveUpAt = Math.max(serverGiveUp, Date.now() + CLI_MIN_CALL_WINDOW_MS);
		this.pollErrors = 0;
		this.store.phase.set('verifyRunning');
		this.startPolling(phoneServiceId, response.attemptId);
	}

	private startPolling(phoneServiceId: number, attemptId: number, firstPollAfterMs = CLI_POLL_INTERVAL_MS): void {
		this.stopPolling();
		this.now.set(Date.now());
		this.ticking = interval(TICK_MS).subscribe(() => {
			this.now.set(Date.now());
			if (this.giveUpAt !== null && Date.now() > this.giveUpAt) {
				this.lastCheck(phoneServiceId, attemptId);
			}
		});
		this.polling = timer(firstPollAfterMs, CLI_POLL_INTERVAL_MS)
			.pipe(exhaustMap(() => this.service.poll$(phoneServiceId, attemptId)))
			.subscribe({
				next: (response) => {
					this.pollErrors = 0;
					this.status.set(response.status);
					if (familyOfStatus(response.status) !== 'running') {
						this.finish(response);
					}
				},
				error: (error: unknown) => {
					if (isUnauthorized(error)) {
						this.stop();
						this.unauthorized$.next();
						return;
					}
					// A refusal will not change by asking again (408/429 are the transient 4xx).
					if (isPermanentClientError(error)) {
						this.giveUp(phoneServiceId, 'poll refused: ' + describe(error));
						return;
					}
					// One failed poll ends this subscription; the attempt is still running on the
					// server, so polling resumes rather than turning a network blip into a result -
					// but not forever.
					this.pollErrors++;
					if (this.pollErrors >= CLI_MAX_POLL_ERRORS) {
						this.giveUp(phoneServiceId, 'poll failed repeatedly: ' + describe(error));
						return;
					}
					this.startPolling(phoneServiceId, attemptId);
				},
			});
	}

	/**
	 * Past the deadline: one last question before giving up. The tick that
	 * notices the deadline can run before the poll that would have carried the result - after a
	 * frozen or throttled tab both are overdue at once - and a VERIFIED call must not be shown as
	 * failed. Only a still-running attempt or a failed request ends in "our side".
	 */
	private lastCheck(phoneServiceId: number, attemptId: number): void {
		this.stopPolling();
		this.finalCheck = this.service
			.poll$(phoneServiceId, attemptId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					this.status.set(response.status);
					if (familyOfStatus(response.status) === 'running') {
						this.giveUp(phoneServiceId, 'no final status before deadline');
						return;
					}
					this.finish(response);
				},
				error: (error: unknown) => {
					if (isUnauthorized(error)) {
						this.stop();
						this.unauthorized$.next();
						return;
					}
					this.giveUp(phoneServiceId, 'no final status before deadline: ' + describe(error));
				},
			});
	}

	/** While a call runs: ask now instead of at the next interval (the tab came back into view). */
	private pollNow(): void {
		const phoneServiceId = this.store.phoneServiceId();
		if (this.polling === null || this.attemptId === null || phoneServiceId === null) {
			return;
		}
		if (this.giveUpAt !== null && Date.now() > this.giveUpAt) {
			this.lastCheck(phoneServiceId, this.attemptId);
			return;
		}
		this.startPolling(phoneServiceId, this.attemptId, 0);
	}

	private finish(response: CliVerificationAttemptResponse): void {
		const family = familyOfStatus(response.status);
		this.logger.usage(LOG_EVENTS.verificationFinished, {
			status: response.status,
			family,
			phoneServiceId: this.store.phoneServiceId(),
		});
		// Cancelled elsewhere (another tab, the server): nothing failed, the customer may start again.
		if (family === 'cancelled') {
			this.backToVerify();
			return;
		}
		this.showResult({
			family,
			retryAt: parseBerlinLocalDateTime(response.lockedUntil)?.toISOString() ?? null,
			remainingTotal: response.remainingTotal ?? null,
		});
	}

	/** No usable end from the server: past the deadline and its grace, refused, or failing repeatedly. */
	private giveUp(phoneServiceId: number, reason: string): void {
		this.logger.error(LOG_EVENTS.failed, {
			stage: 'verification',
			reason,
			phoneServiceId,
		});
		this.showResult({ family: 'ourSide', retryAt: null, remainingTotal: null });
	}

	private showResult(result: VerificationResult): void {
		this.stop();
		if (result.family === 'verified') {
			this.store.numberVerified.set(true);
			this.store.verificationResult.set(null);
			this.store.phase.set('verified');
			this.startAutoContinue();
			return;
		}
		if (result.family === 'declarationMissing') {
			// The declaration lives on the number step; send the customer back there to give it.
			this.store.declarationAccepted.set(false);
			this.store.declarationAttempted.set(true);
			this.store.phase.set('chooseNumber');
			return;
		}
		this.store.verificationResult.set(result);
		this.store.phase.set('verifyResult');
	}

	private backToVerify(then?: () => void): void {
		this.stop();
		this.store.verificationResult.set(null);
		if (then) {
			then();
			return;
		}
		this.store.phase.set('verify');
	}

	private startAutoContinue(): void {
		this.stopAutoContinue();
		this.autoContinueIn.set(VERIFIED_AUTO_CONTINUE_SECONDS);
		this.autoContinue = interval(TICK_MS).subscribe(() => {
			const left = (this.autoContinueIn() ?? 1) - 1;
			if (left <= 0) {
				this.continueToOverview();
				return;
			}
			this.autoContinueIn.set(left);
		});
	}

	private stopAutoContinue(): void {
		this.autoContinue?.unsubscribe();
		this.autoContinue = null;
		this.autoContinueIn.set(null);
	}

	private stopPolling(): void {
		this.polling?.unsubscribe();
		this.polling = null;
		this.finalCheck?.unsubscribe();
		this.finalCheck = null;
		this.ticking?.unsubscribe();
		this.ticking = null;
	}
}

function isUnauthorized(error: unknown): boolean {
	return error instanceof HttpErrorResponse && error.status === 401;
}

/** A 4xx other than 408 (timeout) and 429 (slow down): asking again gets the same answer. */
function isPermanentClientError(error: unknown): boolean {
	return (
		error instanceof HttpErrorResponse &&
		error.status >= 400 &&
		error.status < 500 &&
		error.status !== 408 &&
		error.status !== 429
	);
}

/** A diagnostic for Graylog: the error's kind and status, never a response body. */
function describe(error: unknown): string {
	if (error instanceof HttpErrorResponse) {
		return `HTTP ${error.status}`;
	}
	return error instanceof Error ? error.name : 'unknown';
}
